import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'slices';
import { changePassword, clearChangePasswordState } from 'slices/auth/changePasswordSlice';

type TabType = 'password' | 'pin';

const ChangePassword = () => {
  const [activeTab, setActiveTab] = useState<TabType>('password');
  
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [pinData, setPinData] = useState({
    oldPin: '',
    newPin: '',
    confirmPin: ''
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const dispatch = useDispatch<AppDispatch>();
  const { loading: isLoading, successMessage, error: errorMessage } = useSelector((state: RootState) => state.ChangePassword);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    // Only allow numeric input and max 6 digits for PIN
    if (name === 'oldPin' || name === 'newPin' || name === 'confirmPin') {
      if (/^\d*$/.test(value) && value.length <= 6) {
        setPinData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setPinData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.oldPassword) {
      newErrors.oldPassword = 'Old Password is required';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'New Password is required';
    } else if (formData.newPassword.length < 6) {
      newErrors.newPassword = 'New Password must be at least 6 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Confirm Password is required';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePinForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!pinData.oldPin) {
      newErrors.oldPin = 'Old PIN is required';
    } else if (pinData.oldPin.length !== 4 && pinData.oldPin.length !== 6) {
      newErrors.oldPin = 'Old PIN must be 4 or 6 digits';
    }

    if (!pinData.newPin) {
      newErrors.newPin = 'New PIN is required';
    } else if (pinData.newPin.length !== 4 && pinData.newPin.length !== 6) {
      newErrors.newPin = 'New PIN must be 4 or 6 digits';
    }

    if (!pinData.confirmPin) {
      newErrors.confirmPin = 'Confirm PIN is required';
    } else if (pinData.newPin !== pinData.confirmPin) {
      newErrors.confirmPin = 'PINs do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (activeTab === 'password') {
      if (!validateForm()) {
        return;
      }

      dispatch(clearChangePasswordState());
      const resultAction = await dispatch(changePassword({
        old_password: formData.oldPassword,
        new_password: formData.newPassword
      }));
      if (changePassword.fulfilled.match(resultAction)) {
        setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      }
    } else {
      if (!validatePinForm()) {
        return;
      }
      
      // TODO: Wire to API when endpoint available
      console.log('PIN update:', pinData);
      setPinData({ oldPin: '', newPin: '', confirmPin: '' });
    }
  };

  useEffect(() => {
    return () => {
      dispatch(clearChangePasswordState());
    };
  }, [dispatch]);

  return (
    <div className="page-content">
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow border border-gray-100">
            <div className="px-5 sm:px-6 py-4 border-b border-gray-100">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h4>
              
              {/* Dropdown/Tabs */}
              <div className="flex gap-2 border-b border-gray-200 -mb-4">
                <button
                  type="button"
                  onClick={() => setActiveTab('password')}
                  className={`px-4 py-2 font-medium text-sm transition-colors relative ${
                    activeTab === 'password'
                      ? 'text-indigo-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Change Password
                  {activeTab === 'password' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('pin')}
                  className={`px-4 py-2 font-medium text-sm transition-colors relative ${
                    activeTab === 'pin'
                      ? 'text-indigo-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Update PIN
                  {activeTab === 'pin' && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"></div>
                  )}
                </button>
              </div>
            </div>
            <div className="px-5 sm:px-6 py-6">
              {successMessage && (
                <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-800">
                  {successMessage}
                </div>
              )}

              {errorMessage && (
                <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Change Password Form */}
                {activeTab === 'password' && (
                  <>
                <div>
                  <label htmlFor="oldPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Old Password
                  </label>
                  <input
                    type="password"
                    id="oldPassword"
                    name="oldPassword"
                    value={formData.oldPassword}
                    onChange={handleInputChange}
                    placeholder="Enter old password"
                    className={`block w-full md:w-1/2 rounded-md border bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.oldPassword ? 'border-red-300' : 'border-gray-200'}`}
                  />
                  {errors.oldPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.oldPassword}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="Enter new password"
                    className={`block w-full md:w-1/2 rounded-md border bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.newPassword ? 'border-red-300' : 'border-gray-200'}`}
                  />
                  {errors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm new password"
                    className={`block w-full md:w-1/2 rounded-md border bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.confirmPassword ? 'border-red-300' : 'border-gray-200'}`}
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                </div>
                  </>
                )}

                {/* Update PIN Form */}
                {activeTab === 'pin' && (
                  <>
                <div>
                  <label htmlFor="oldPin" className="block text-sm font-medium text-gray-700 mb-1">
                    Old PIN
                  </label>
                  <input
                    type="password"
                    id="oldPin"
                    name="oldPin"
                    value={pinData.oldPin}
                    onChange={handlePinInputChange}
                    placeholder="Enter old PIN"
                    className={`block w-full md:w-1/2 rounded-md border bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.oldPin ? 'border-red-300' : 'border-gray-200'}`}
                    pattern="[0-9]*"
                    maxLength={6}
                  />
                  {errors.oldPin && (
                    <p className="mt-1 text-sm text-red-600">{errors.oldPin}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="newPin" className="block text-sm font-medium text-gray-700 mb-1">
                    New PIN
                  </label>
                  <input
                    type="password"
                    id="newPin"
                    name="newPin"
                    value={pinData.newPin}
                    onChange={handlePinInputChange}
                    placeholder="Enter new PIN"
                    className={`block w-full md:w-1/2 rounded-md border bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.newPin ? 'border-red-300' : 'border-gray-200'}`}
                    pattern="[0-9]*"
                    maxLength={6}
                  />
                  {errors.newPin && (
                    <p className="mt-1 text-sm text-red-600">{errors.newPin}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPin" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm PIN
                  </label>
                  <input
                    type="password"
                    id="confirmPin"
                    name="confirmPin"
                    value={pinData.confirmPin}
                    onChange={handlePinInputChange}
                    placeholder="Confirm new PIN"
                    className={`block w-full md:w-1/2 rounded-md border bg-gray-50 px-3 py-2 text-gray-900 placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${errors.confirmPin ? 'border-red-300' : 'border-gray-200'}`}
                    pattern="[0-9]*"
                    maxLength={6}
                  />
                  {errors.confirmPin && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPin}</p>
                  )}
                </div>
                  </>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-60"
                  >
                    {isLoading ? 'Updating...' : activeTab === 'password' ? 'Change' : 'Update'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
